import React from "react";
import Modal from "react-modal";
import moment from "moment";

import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { DumpEvent, OrderStatus } from "../../../models";
import { getOrders } from "../../Orders/ordersSlice";
import { updateDumpEvent } from "../dumpEventsSlice";

import "./ReassignOrderModal.scss";

const modalStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    backgroundColor: "#FFFFFF",
    maxWidth: "460px",
    width: "90%",
    padding: "0",
  },
};

// Every order status — the dropdown lists every order with a pickup on the
// receipt's day regardless of status (a dumped box is often already
// picked-up/complete, so narrowing by status would hide the likely match).
const ALL_STATUSES = Object.values(OrderStatus);

interface Props {
  // The receipt whose order link is being changed; null = modal closed.
  event: DumpEvent | null;
  onClose: () => void;
  // Called after a successful reassign so the caller can refresh the list.
  onSaved: () => void;
}

// Reattribute a dump receipt to a different order via PATCH /dump_events/{id}.
// The order list is built the same way as the dumpster Set-Location modal (a
// native <select> of "name - address, city"), scoped to orders whose pickup
// falls on the receipt's own day.
export function ReassignOrderModal({ event, onClose, onSaved }: Props) {
  const dispatch = useAppDispatch();
  const orders = useAppSelector((s) => s.orders.orders);
  const [selectedOrderId, setSelectedOrderId] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // The receipt's own day (when the dumpster was dumped). We list orders whose
  // pickup is that day — the box dumped that day was picked up around then.
  const receiptDay = event ? moment(event.createdAt).format("YYYY-MM-DD") : "";

  React.useEffect(() => {
    if (!event) return;
    setSelectedOrderId(event.orderId ?? "");
    setError(null);
    dispatch(
      getOrders({
        pagination: { offset: 0, count: 200 },
        filters: {
          statuses: ALL_STATUSES,
          pickupStartDate: receiptDay,
          pickupEndDate: receiptDay,
        },
      })
    );
  }, [event, dispatch, receiptDay]);

  // Options = orders with a pickup on the receipt's day. If the receipt is
  // already linked to an order outside that set (e.g. a mis-dated attribution),
  // surface it too so the admin can see what it's on now.
  const options = React.useMemo(() => {
    const opts = (orders || []).map((o) => ({
      id: o.id,
      label: `${o.name} - ${o.address}, ${o.city}`,
    }));
    if (event?.orderId && !opts.some((o) => o.id === event.orderId)) {
      const addr = [event.orderAddress, event.orderCity]
        .filter(Boolean)
        .join(", ");
      opts.unshift({
        id: event.orderId,
        label: `${event.orderName || `Order ${event.orderId}`}${
          addr ? ` - ${addr}` : ""
        } (current)`,
      });
    }
    return opts;
  }, [orders, event]);

  const unchanged = selectedOrderId === (event?.orderId ?? "");

  const save = async () => {
    if (!event || !selectedOrderId || unchanged) return;
    setSaving(true);
    setError(null);
    try {
      await dispatch(
        updateDumpEvent({ id: event.id, update: { orderId: selectedOrderId } })
      ).unwrap();
      onSaved();
      onClose();
    } catch {
      setError("Could not reassign. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={event !== null}
      onRequestClose={onClose}
      style={modalStyles}
      contentLabel="Reassign dump receipt order"
    >
      <div className="reassign-order-modal">
        <div className="reassign-order-header">
          <h3>Reassign order</h3>
          <button
            className="reassign-order-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="reassign-order-body">
          <label className="reassign-field">
            <span>
              Order
              {event && ` — pickups on ${moment(event.createdAt).format(
                "ddd, Do MMM YYYY"
              )}`}
            </span>
            <select
              className="select-input"
              value={selectedOrderId}
              onChange={(e) => setSelectedOrderId(e.target.value)}
            >
              <option value="">-- Select an order --</option>
              {options.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          {options.length === 0 && (
            <div className="reassign-empty">
              No orders with pickups on this date.
            </div>
          )}
          {error && <div className="reassign-error">{error}</div>}
        </div>
        <div className="reassign-order-footer">
          <button className="btn btn-cancel" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            className="btn btn-save"
            onClick={save}
            disabled={saving || !selectedOrderId || unchanged}
          >
            {saving ? "Saving…" : "Reassign"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
